import { useZero } from "@rocicorp/zero/react";
import { useMemo } from "react";
import { findConnectorTypes } from "../../shared/connectors/findConnectorTypes";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import { useNavigateOnBranchChange } from "../hooks/useNavigateOnBranchChange";
import { useQuery } from "../hooks/useQuery";
import { navigate, routes, useCurrentUrl } from "../lib/urls";
import { type SelectableConnector } from "../hooks/useConnectorSelectOnSave";
import { ConnectorPrompt } from "../tools/ConnectorPrompt";

type Props = {
  claimId: string;
  onClose: () => void;
};

export function ConnectClaimPrompt({ claimId, onClose }: Props) {
  const z = useZero();
  const [claim] = useQuery(queries.loadConnectionClaim({ claimId }));
  const {
    spaceId,
    workflowId,
    step: urlStep,
  } = useCurrentUrl(routes.space.workflow);
  const navigateOnBranchChange = useNavigateOnBranchChange(
    claim?.branchId ?? ""
  );

  const appIdentifiers = useMemo(() => {
    if (!claim) return [];
    return findConnectorTypes(claim).map((a) => a.identifier);
  }, [claim]);

  if (!claim || claim.status !== "pending") return null;

  return (
    <ConnectorPrompt
      pending={{
        toolCallId: claim.id,
        step: claim.stepDir,
        targetUrl: claim.targetUrl ?? undefined,
        search: claim.search ?? undefined,
        appIdentifiers,
      }}
      spaceId={spaceId}
      openCreatedApp={(connectedAppId) => {
        navigate(routes.modal.connector, { id: connectedAppId });
      }}
      onSelectApp={async (app: SelectableConnector) => {
        const forkBranchId = await navigateOnBranchChange((branchParams) =>
          z.mutate(
            sharedMutators.associateStepWithConnectedApp({
              ...branchParams,
              stepDir: claim.stepDir,
              connectedAppId: app.id,
              silent: true,
            })
          )
        );
        if (!forkBranchId) {
          navigate(
            routes.space.workflow,
            { spaceId, workflowId, step: urlStep },
            { replace: true }
          );
        }
        void z.mutate(
          sharedMutators.resolveConnectionClaim({
            claimId: claim.id,
            connectedAppId: app.id,
            branchId: forkBranchId ?? claim.branchId,
          })
        );
        onClose();
      }}
      onDismiss={() => {
        void z.mutate(
          sharedMutators.dismissConnectionClaim({ claimId: claim.id })
        );
        onClose();
      }}
      onNoApps={() => {
        void z.mutate(
          sharedMutators.dismissConnectionClaim({ claimId: claim.id })
        );
        onClose();
      }}
      onTypeInstead={() => {
        void z.mutate(
          sharedMutators.dismissConnectionClaim({ claimId: claim.id })
        );
        onClose();
      }}
    />
  );
}
